import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Menu,
  Users,
  UserCircle,
  Store,
  MapPin,
  Palette,
  MessageSquare,
  PanelLeft,
} from "lucide-react";
import { cn } from "../../../foundation/altzero_base/base/lib/utils";
import { useIsMobile } from "../../../foundation/altzero_base/base/components/ui/use-mobile";
import { Sheet, SheetContent } from "../../../foundation/altzero_base/base/components/ui/sheet";
import { useUserRole } from "../../../foundation/altzero_base/base/hooks/useUserRole";
import { usePermissions } from "../../../foundation/altzero_base/base/contextapi/PermissionsContext";

// Base navigation items available to all users
const baseNavigationItems = [
  {
    name: "Dashboard",
    href: "/tender/dashboard",
    icon: LayoutDashboard,
  },
  {
    name: "Tenders", // Changed from "Proposals" to "Tenders"
    href: "/tender/proposals",
    icon: FileText,
  },
  {
    name: "Email",
    href: "/tender/email",
    icon: MessageSquare,
  },
  {
    name: "Clients",
    href: "/tender/resource-library/clients",
    icon: Users,
  },
  {
    name: "Contacts",
    href: "/tender/resource-library/contacts",
    icon: UserCircle,
  },
  // {
  //   name: "Sites",
  //   href: "/tender/resource-library/sites",
  //   icon: MapPin,
  // },
  // {
  //   name: "Tender Management",
  //   href: "/tender/resource-library/tenders",
  //   icon: FileText,
  // },
];

// Org admin + system admin items
const orgAdminNavigationItems = [
  {
    name: "Retailers",
    href: "/tender/resource-library/retailers",
    icon: Store,
  },
];

// System admin only items — moved to Admin Portal
// const sysAdminNavigationItems = [
//   {
//     name: "Templates",
//     href: "/tender/templates",
//     icon: FileText,
//   },
//   {
//     name: "Prompt Template Config",
//     href: "/tender/prompt-template-config",
//     icon: MessageSquare,
//   },
// ];

interface ScopingAISidebarProps {
  forceCollapsed?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function ScopingAISidebar({
  forceCollapsed = false,
  mobileOpen = false,
  onMobileClose = () => { }
}: ScopingAISidebarProps = {}) {
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(forceCollapsed);
  const isMobile = useIsMobile();
  const { role } = useUserRole();
  const { isAdmin: isOrgAdmin } = usePermissions();
  const isSysAdmin = role === 'admin';

  // Sync with forceCollapsed prop when it changes
  useEffect(() => {
    setSidebarCollapsed(forceCollapsed);
  }, [forceCollapsed]);

  // Use local state for collapse status
  const isCollapsed = sidebarCollapsed;

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const navigationItems = [
    ...baseNavigationItems,
    ...(isOrgAdmin || isSysAdmin ? orgAdminNavigationItems : []),
  ];

  const isActive = (href: string) => {
    if (href === "/tender/dashboard") {
      return (
        location.pathname === "/tender" ||
        location.pathname === "/tender/dashboard"
      );
    }
    return (
      location.pathname === href || location.pathname.startsWith(href + "/")
    );
  };



  // Sidebar content component (reused for both mobile and desktop)
  const SidebarContent = () => (
    <>
      {/* Header */}
      <div className={cn(
        "border-b border-gray-200 transition-all duration-300",
        isCollapsed ? "p-3" : "p-4 md:p-6"
      )}>
        <div className={cn(
          "flex items-center",
          isCollapsed ? "flex-col gap-2" : "justify-between"
        )}>
          <Link
            to="/tender/dashboard"
            className={cn(
              "flex items-center transition-all duration-300",
              isCollapsed ? "justify-center" : "space-x-3"
            )}
            title={isCollapsed ? "ChoiceEnergy - AI-Powered Tender" : undefined}
            onClick={() => isMobile && onMobileClose()}
          >
            {!isCollapsed && (
              <div>
                <h1 className="text-lg md:text-xl font-bold text-gray-900">ChoiceEnergy</h1>
                <p className="text-xs md:text-sm text-gray-600">AI-Powered Tender</p>
              </div>
            )}
          </Link>

          {!isMobile && (
            <button
              onClick={toggleSidebar}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <PanelLeft className="h-5 w-5 text-gray-600" />
              ) : (
                <Menu className="h-5 w-5 text-gray-600" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className={cn(
        "flex-1 space-y-2 overflow-y-auto transition-all duration-300",
        isCollapsed ? "p-2" : "p-2 md:p-4"
      )}>
        {navigationItems.map((item) => (
          <div key={item.name}>
            <Link
              to={item.href}
              className={cn(
                "flex items-center rounded-lg text-sm font-medium transition-all duration-200",
                isCollapsed
                  ? "justify-center p-3 mx-auto w-12 h-12"
                  : "px-3 py-2 space-x-3",
                isActive(item.href)
                  ? "bg-blue-50 text-blue-700 border border-blue-200 shadow-sm"
                  : "text-gray-700 hover:bg-gray-100 hover:shadow-sm"
              )}
              title={isCollapsed ? item.name : undefined}
              onClick={() => isMobile && onMobileClose()}
            >
              <item.icon className={cn(
                "transition-all duration-200",
                isCollapsed ? "h-6 w-6" : "h-5 w-5"
              )} />
              {!isCollapsed && <span>{item.name}</span>}
            </Link>


          </div>
        ))}
      </nav>
    </>
  );

  // Mobile: Sheet drawer
  if (isMobile) {
    return (
      <Sheet open={mobileOpen} onOpenChange={onMobileClose}>
        <SheetContent side="left" className="w-64 p-0">
          <div className="flex flex-col h-full bg-white">
            <SidebarContent />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Fixed sidebar
  return (
    <div
      className={cn(
        "hidden md:flex bg-white border-r border-gray-200 flex-col transition-all duration-300 ease-in-out",
        isCollapsed ? "w-16" : "w-64"
      )}
    >
      <SidebarContent />
    </div>
  );
}
